import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, CreditCard, Wallet, Smartphone, Calculator, Trash2, ShoppingCart, Plus } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import AddFnBModal from './AddFnBModal';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PaymentDialog({ rental, open, onClose, onPaymentSuccess }) {
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [amountReceived, setAmountReceived] = useState('');
  const [change, setChange] = useState(0);
  const [processing, setProcessing] = useState(false);
  const [existingItems, setExistingItems] = useState([]);
  const [newItems, setNewItems] = useState([]);
  const [showAddFnBModal, setShowAddFnBModal] = useState(false);
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [removingItemIndex, setRemovingItemIndex] = useState(-1);

  // Load existing additional items from rental (already saved in DB)
  useEffect(() => {
    if (rental?.additional_items) {
      setExistingItems(rental.additional_items);
    }
  }, [rental]);

  // Fetch products when dialog opens
  useEffect(() => {
    if (open) {
      fetchProducts();
    }
  }, [open]);

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setProducts(response.data.products);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      setProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  // Use final_price for completed rentals (includes actual duration calculation for no_limit mode)
  // Calculate totals
  const existingItemsTotal = existingItems.reduce((sum, item) => sum + (item.price * item.qty), 0);
  const newItemsTotal = newItems.reduce((sum, item) => sum + (item.price * item.qty), 0);
  const rentalPrice = rental?.final_price || rental?.total_price || 0;
  
  // Base rental price = rentalPrice - existing F&B only
  // Because backend's final_price already includes existing F&B items
  const baseRentalPrice = rentalPrice - existingItemsTotal;
  
  // Total amount = rental price + new items (rental price already includes existing items)
  const totalAmount = rentalPrice + newItemsTotal;
  const allItems = [...existingItems, ...newItems];
  
  // Debug logging
  console.log(' PaymentDialog Calculation:', {
    rentalPrice,
    existingItemsTotal,
    newItemsTotal,
    baseRentalPrice,
    totalAmount,
    existingItemsCount: existingItems.length,
    newItemsCount: newItems.length
  });

  useEffect(() => {
    if (amountReceived) {
      const received = parseFloat(amountReceived) || 0;
      const changeAmount = received - totalAmount;
      setChange(changeAmount > 0 ? changeAmount : 0);
    } else {
      setChange(0);
    }
  }, [amountReceived, totalAmount]);

  const handleQuickAmount = (amount) => {
    setAmountReceived(amount.toString());
  };

  const handleExactAmount = () => {
    setAmountReceived(totalAmount.toString());
  };

  const handleAddItemsFromModal = (items) => {
    // Convert items from modal to format needed
    const formattedItems = items.map(item => ({
      id: Date.now() + Math.random(), // Unique ID
      product_id: item.id,
      name: item.name,
      price: item.price,
      qty: item.qty,
      type: item.type || 'simple',
      category: item.category
    }));

    setNewItems([...newItems, ...formattedItems]);
  };

  const handleRemoveItem = (id) => {
    // Only allow removing new items, not existing ones
    setNewItems(newItems.filter(item => item.id !== id));
  };

  const handleRemoveExistingItem = async (itemIndex) => {
    if (!window.confirm('Hapus item ini dari rental? Stok akan dikembalikan.')) {
      return;
    }

    setRemovingItemIndex(itemIndex);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(
        `${API}/api/rentals/${rental.id}/remove-order-item`,
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          data: { item_index: itemIndex }
        }
      );

      if (response.data.success) {
        toast.success('Item berhasil dihapus dan stok dikembalikan!');
        
        // Update local state - remove item from existingItems
        const updatedExistingItems = existingItems.filter((_, index) => index !== itemIndex);
        setExistingItems(updatedExistingItems);
        
        // Notify parent to refresh rental data
        if (onPaymentSuccess) {
          // We need to trigger a refresh of the rental data in parent
          // The parent will need to re-fetch the rental
          console.log(' Item deleted, parent should refresh rental data');
        }
      } else {
        toast.error(response.data.error || 'Gagal menghapus item');
      }
    } catch (error) {
      console.error('Error removing item:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Gagal menghapus item';
      toast.error(errorMsg);
    } finally {
      setRemovingItemIndex(-1);
    }
  };

  const handleSubmitPayment = async () => {
    if (paymentMethod === 'cash') {
      const received = parseFloat(amountReceived) || 0;
      if (received < totalAmount) {
        toast.error('Uang yang diterima kurang dari total tagihan!');
        return;
      }
    }

    setProcessing(true);
    try {
      console.log(' Submitting payment:', {
        rental_id: rental.id,
        paymentMethod,
        amountReceived: parseFloat(amountReceived) || totalAmount,
        newItems
      });
      
      await onPaymentSuccess(
        rental.id, 
        paymentMethod, 
        parseFloat(amountReceived) || totalAmount,
        newItems
      );
      
      console.log(' Payment success!');
      toast.success('Pembayaran berhasil!');
      onClose();
    } catch (error) {
      console.error(' Payment failed:', error);
      console.error('   Error details:', error.response?.data);
      const errorMsg = error.response?.data?.detail || error.message || 'Gagal memproses pembayaran';
      toast.error(errorMsg);
    } finally {
      setProcessing(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  // Lock body scroll when modal is open (NO AUTO SCROLL!)
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [open]);

  if (!open) return null;

  const modalContent = (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[9999] p-4" onClick={(e) => {
      if (e.target === e.currentTarget) onClose();
    }}>
      <div className="billing-payment-dialog bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header - Gaming Theme */}
        <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-blue-600 p-4 text-white flex-shrink-0 border-b-2 border-blue-400/50">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <CreditCard className="h-6 w-6 animate-pulse" />
                Proses Pembayaran
              </h2>
              <p className="text-blue-100 text-xs mt-1 font-semibold">Unit: {rental?.ps_unit_name}</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 bg-white/20 hover:bg-white/30 rounded-full transition"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Body - Scrollable */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4" style={{scrollbarWidth: 'none', msOverflowStyle: 'none'}}>
          <style>{`
            .overflow-y-auto::-webkit-scrollbar {
              display: none;
            }
          `}</style>

          {/* Detail Transaksi - Gaming Theme */}
          <div className="bg-gradient-to-br from-blue-500/20 to-purple-500/20 border-2 border-blue-400/30 rounded-xl p-3 space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-blue-200 text-sm font-semibold">Customer</span>
              <span className="font-bold text-white">{rental?.customer_name}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-blue-200 text-sm font-semibold">Telepon</span>
              <span className="font-bold text-white text-sm">{rental?.customer_phone}</span>
            </div>
            <div className="flex justify-between items-center bg-slate-900/50 px-2 py-1.5 rounded-lg">
              <span className="text-blue-200 text-sm font-semibold">PS Unit</span>
              <span className="font-black text-white text-lg">{rental?.ps_unit_name}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-blue-200 text-sm font-semibold">Durasi</span>
              <span className="font-bold text-white text-sm">
                {rental?.actual_duration_hours 
                  ? `${Math.round(rental.actual_duration_hours * 60)} menit`
                  : rental?.duration_minutes 
                    ? `${rental.duration_minutes} menit`
                    : '-'
                }
              </span>
            </div>
            <div className="flex justify-between items-center pt-2 border-t border-blue-400/30">
              <span className="text-blue-200 text-sm font-semibold">Harga Rental</span>
              <span className="font-bold text-green-300 text-lg">{formatCurrency(baseRentalPrice)}</span>
            </div>
          </div>

          {/* Additional Items Section - Gaming Theme */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-bold text-orange-300 flex items-center gap-1.5 uppercase tracking-wider">
                <ShoppingCart className="h-4 w-4 animate-pulse" />
                 Pesanan Tambahan
              </label>
              <button
                onClick={() => setShowAddFnBModal(true)}
                disabled={loadingProducts}
                className="px-3 py-2 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white text-xs font-black rounded-lg flex items-center gap-1.5 shadow-lg hover:shadow-orange-500/50 transform hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Plus className="h-4 w-4" />
                {loadingProducts ? 'Memuat...' : 'Tambah Item'}
              </button>
            </div>

            {/* Items List - removed inline form */}
            {false && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-3 space-y-3">
                {loadingProducts ? (
                  <div className="text-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto"></div>
                    <p className="text-xs text-gray-500 mt-2">Memuat produk...</p>
                  </div>
                ) : products.length === 0 ? (
                  <div className="text-center py-6 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-xs text-gray-700 mb-1">Belum ada produk yang tersedia.</p>
                    <p className="text-xs text-gray-500">
                      Admin belum menambahkan produk ke katalog.
                    </p>
                  </div>
                ) : (
                  <>
                    <div>
                      <label className="text-xs text-gray-600 mb-1 block font-medium">Pilih Produk *</label>
                      <select
                        value={selectedProduct}
                        onChange={(e) => setSelectedProduct(e.target.value)}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                      >
                        <option value="">-- Pilih Produk --</option>
                        {products.map((product) => (
                          <option key={product.id} value={product.id}>
                            {product.name} - Rp {product.price.toLocaleString('id-ID')} 
                            {product.type === 'composite' ? ' (Komposit)' : ` (Stok: ${product.stock})`}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    {selectedProduct && (() => {
                      const selectedProd = products.find(p => p.id === selectedProduct);
                      return (
                        <div className="bg-white border border-blue-300 rounded-lg p-3">
                          <div className="flex justify-between items-center">
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-800">
                                {selectedProd?.name}
                              </p>
                              <p className="text-xs text-gray-600">
                                Kategori: {selectedProd?.category}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-base font-bold text-blue-600">
                                Rp {selectedProd?.price.toLocaleString('id-ID')}
                              </p>
                              <p className="text-xs text-gray-600">
                                {selectedProd?.type === 'composite' ? 'Komposit' : `Stok: ${selectedProd?.stock}`}
                              </p>
                            </div>
                          </div>
                          
                          {/* Show ingredients for composite products */}
                          {selectedProd?.type === 'composite' && selectedProd?.recipe && selectedProd.recipe.length > 0 && (
                            <div className="mt-3 pt-3 border-t border-blue-200">
                              <p className="text-xs font-semibold text-gray-700 mb-2"> Bahan yang dibutuhkan:</p>
                              <div className="space-y-1">
                                {selectedProd.recipe.map((ing, idx) => {
                                  // Display infinity icon for non-stock ingredients
                                  if (ing.is_non_stock) {
                                    return (
                                      <div key={idx} className="flex items-center justify-between text-xs">
                                        <span className="text-gray-700">
                                          • {ing.name} ({ing.quantity} {ing.unit})
                                        </span>
                                        <span className="text-blue-600 font-medium">
                                           Unlimited
                                        </span>
                                      </div>
                                    );
                                  }
                                  
                                  const isLowStock = ing.stock < 10;
                                  const isOutOfStock = ing.stock <= 0;
                                  return (
                                    <div key={idx} className="flex items-center justify-between text-xs">
                                      <span className="text-gray-700">
                                        • {ing.name} ({ing.quantity} {ing.unit})
                                      </span>
                                      <span className={`font-medium ${
                                        isOutOfStock ? 'text-red-600' : 
                                        isLowStock ? 'text-amber-600' : 
                                        'text-green-600'
                                      }`}>
                                        {isOutOfStock ? ' Habis' : isLowStock ? ` ${ing.stock}` : ` ${ing.stock}`}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })()}

                    <div className="flex gap-2">
                      <div className="flex-1">
                        <label className="text-xs text-gray-600 mb-1 block font-medium">Jumlah</label>
                        <input
                          type="number"
                          value={newItemQty}
                          onChange={(e) => setNewItemQty(e.target.value)}
                          min="1"
                          className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                        />
                      </div>
                      <div className="flex items-end gap-2">
                        <button
                          onClick={handleAddItem}
                          disabled={!selectedProduct}
                          className={`px-4 py-2.5 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 ${
                            selectedProduct 
                              ? 'bg-blue-600 text-white hover:bg-blue-700' 
                              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          }`}
                        >
                          <Plus className="h-4 w-4" />
                          Tambah
                        </button>
                        <button
                          onClick={() => {
                            setShowAddItem(false);
                            setSelectedProduct('');
                            setNewItemQty(1);
                          }}
                          className="px-4 py-2.5 bg-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-300"
                        >
                          Batal
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Items List */}
            {allItems.length > 0 && (
              <div className="space-y-2">
                {allItems.map((item, index) => {
                  const isExisting = index < existingItems.length;
                  const existingItemIndex = isExisting ? index : null;
                  // Create unique key: existing items use "existing-{index}", new items use "new-{id}"
                  const uniqueKey = isExisting ? `existing-${index}` : `new-${item.id || index}`;
                  return (
                    <div key={uniqueKey} className={`flex items-center justify-between border rounded-lg p-3 ${
                      isExisting ? 'bg-gray-50 border-gray-200' : 'bg-blue-50 border-blue-200'
                    }`}>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">
                          {item.name}
                          {!isExisting && <span className="ml-2 text-xs bg-blue-500 text-white px-2 py-1 rounded">BARU</span>}
                        </p>
                        <p className="text-sm text-gray-500">
                          {formatCurrency(item.price)} × {item.qty}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-semibold text-blue-600">
                        {formatCurrency(item.price * item.qty)}
                      </span>
                        <button
                          onClick={() => isExisting ? handleRemoveExistingItem(existingItemIndex) : handleRemoveItem(item.id)}
                          disabled={removingItemIndex === existingItemIndex}
                          className="text-red-500 hover:text-red-600 p-1 hover:bg-red-50 rounded transition disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Hapus item (stok akan dikembalikan)"
                        >
                          {removingItemIndex === existingItemIndex ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-red-500 border-t-transparent"></div>
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </div>
                  );
                })}
                {/* Subtotal F&B only */}
                {allItems.length > 0 && (
                  <div className="flex justify-between items-center pt-3 mt-2 border-t-2 border-blue-400/50 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-lg px-3 py-2">
                    <span className="text-sm font-black text-blue-300 uppercase tracking-wider"> Total F&B</span>
                    <span className="font-black text-blue-300 text-xl">{formatCurrency(existingItemsTotal + newItemsTotal)}</span>
                  </div>
                )}
              </div>
            )}

            {allItems.length === 0 && (
              <p className="text-sm text-gray-400 text-center py-4">Belum ada pesanan tambahan</p>
            )}
          </div>

          {/* Total Akhir */}
          <div className="bg-gradient-to-br from-white to-blue-50 rounded-xl p-4 border-2 border-blue-400 shadow-lg">
            <div className="flex justify-between items-center">
              <span className="text-base font-black text-gray-800 uppercase tracking-wide"> Total Tagihan</span>
              <span className="text-2xl font-black text-blue-600 drop-shadow-lg">
                {formatCurrency(totalAmount)}
              </span>
            </div>
          </div>

          {/* Metode Pembayaran - Gaming Theme */}
          <div>
            <label className="block text-xs font-bold text-blue-200 mb-2 uppercase tracking-wider">
               Metode Pembayaran
            </label>
            <div className="grid grid-cols-3 gap-2">
              <button
                onClick={() => setPaymentMethod('cash')}
                className={`p-3 rounded-lg border-2 transition-all transform hover:scale-105 ${
                  paymentMethod === 'cash'
                    ? 'border-green-400 bg-gradient-to-br from-green-500/50 to-green-600/50 shadow-lg shadow-green-500/30'
                    : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'
                }`}
              >
                <Wallet className={`h-5 w-5 mx-auto mb-1 ${
                  paymentMethod === 'cash' ? 'text-green-300' : 'text-slate-500'
                }`} />
                <span className={`text-xs font-bold ${
                  paymentMethod === 'cash' ? 'text-white' : 'text-slate-400'
                }`}>
                  Cash
                </span>
              </button>

              <button
                onClick={() => setPaymentMethod('qris')}
                className={`p-3 rounded-lg border-2 transition-all transform hover:scale-105 ${
                  paymentMethod === 'qris'
                    ? 'border-blue-400 bg-gradient-to-br from-blue-500/50 to-blue-600/50 shadow-lg shadow-blue-500/30'
                    : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'
                }`}
              >
                <Smartphone className={`h-5 w-5 mx-auto mb-1 ${
                  paymentMethod === 'qris' ? 'text-blue-300' : 'text-slate-500'
                }`} />
                <span className={`text-xs font-bold ${
                  paymentMethod === 'qris' ? 'text-white' : 'text-slate-400'
                }`}>
                  QRIS
                </span>
              </button>

              <button
                onClick={() => setPaymentMethod('transfer')}
                className={`p-3 rounded-lg border-2 transition-all transform hover:scale-105 ${
                  paymentMethod === 'transfer'
                    ? 'border-purple-400 bg-gradient-to-br from-purple-500/50 to-purple-600/50 shadow-lg shadow-purple-500/30'
                    : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'
                }`}
              >
                <CreditCard className={`h-5 w-5 mx-auto mb-1 ${
                  paymentMethod === 'transfer' ? 'text-purple-300' : 'text-slate-500'
                }`} />
                <span className={`text-xs font-bold ${
                  paymentMethod === 'transfer' ? 'text-white' : 'text-slate-400'
                }`}>
                  Transfer
                </span>
              </button>
            </div>
          </div>

          {/* Input Uang Diterima (Hanya untuk Cash) */}
          {paymentMethod === 'cash' && (
            <div className="bg-white rounded-xl p-4 border-2 border-gray-200 shadow-lg">
              <label className="block text-sm font-black text-gray-800 mb-3 uppercase tracking-wide flex items-center gap-2">
                <Calculator className="h-4 w-4 text-blue-600" />
                 Uang Diterima
              </label>
              
              {/* Quick Amount Buttons */}
              <div className="grid grid-cols-4 gap-3 mb-4">
                <button
                  onClick={handleExactAmount}
                  className={`px-4 py-3 rounded-xl text-sm font-bold transition-all transform hover:scale-105 ${
                    amountReceived === totalAmount.toString()
                      ? 'bg-gradient-to-br from-green-500 to-green-600 text-white shadow-lg shadow-green-500/50 ring-2 ring-green-400'
                      : 'bg-gradient-to-br from-green-400 to-green-500 text-white hover:from-green-500 hover:to-green-600 shadow-md'
                  }`}
                >
                   Pas
                </button>
                {[10000, 20000, 50000].map((amount) => (
                  <button
                    key={amount}
                    onClick={() => handleQuickAmount(amount)}
                    className={`px-4 py-3 rounded-xl text-sm font-bold transition-all transform hover:scale-105 ${
                      amountReceived === amount.toString()
                        ? 'bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-500/50 ring-2 ring-blue-400'
                        : 'bg-gradient-to-br from-gray-100 to-gray-200 hover:from-gray-200 hover:to-gray-300 text-gray-800 shadow-md'
                    }`}
                  >
                    {amount >= 1000 ? `Rp ${(amount/1000)}K` : `Rp ${amount}`}
                  </button>
                ))}
              </div>

              {/* Input Field */}
              <div className="relative">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-bold text-lg">Rp</div>
                <input
                  type="number"
                  value={amountReceived}
                  onChange={(e) => setAmountReceived(e.target.value)}
                  placeholder="0"
                  className="w-full pl-14 pr-4 py-4 text-2xl font-bold border-3 border-blue-300 rounded-xl focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-200 bg-blue-50"
                />
              </div>

              {/* Kembalian */}
              {amountReceived && (
                <div className={`mt-5 p-6 rounded-2xl border-3 ${
                  change < 0 
                    ? 'bg-gradient-to-br from-red-50 to-red-100 border-red-400' 
                    : 'bg-gradient-to-br from-green-50 to-emerald-100 border-green-400 shadow-lg'
                }`}>
                  <div className="flex justify-between items-center">
                    <span className={`font-black text-lg uppercase tracking-wide ${
                      change < 0 ? 'text-red-800' : 'text-green-800'
                    }`}>
                      {change < 0 ? ' Uang Kurang' : ' Kembalian'}
                    </span>
                    <span className={`text-4xl font-black drop-shadow-md ${
                      change < 0 ? 'text-red-600' : 'text-green-600'
                    }`}>
                      {formatCurrency(Math.abs(change))}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Non-Cash Payment Info */}
          {paymentMethod !== 'cash' && (
            <div className="p-5 bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-xl shadow-md">
              <p className="text-sm text-blue-900 font-semibold">
                 Pastikan customer sudah melakukan pembayaran via <span className="font-black uppercase">{paymentMethod === 'qris' ? 'QRIS' : 'Transfer Bank'}</span> sebesar:
              </p>
              <p className="text-2xl font-black text-blue-600 mt-2">
                {formatCurrency(totalAmount)}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gradient-to-r from-slate-800 to-slate-900 px-6 py-5 flex gap-4 justify-end border-t-2 border-blue-500/50">
          <button
            onClick={onClose}
            disabled={processing}
            className="px-8 py-3 border-2 border-slate-600 bg-slate-700 hover:bg-slate-600 text-white rounded-xl transition-all font-bold disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105"
          >
             Batal
          </button>
          <button
            onClick={handleSubmitPayment}
            disabled={processing || (paymentMethod === 'cash' && change < 0)}
            className="px-10 py-3 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-xl transition-all font-black shadow-lg shadow-green-500/50 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none transform hover:scale-105"
          >
            {processing ? ' Memproses...' : ' Konfirmasi Pembayaran'}
          </button>
        </div>
      </div>

      {/* Add F&B Modal */}
      <AddFnBModal
        open={showAddFnBModal}
        onClose={() => setShowAddFnBModal(false)}
        products={products}
        onAddItems={handleAddItemsFromModal}
        onFavoriteToggle={fetchProducts}
      />
    </div>
  );

  // Render modal using Portal to ensure it's at document body level
  return createPortal(modalContent, document.body);
}
